import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/services", label: "Services", testId: "nav-services" },
  { to: "/borrowers-investors", label: "Borrowers & Investors", testId: "nav-borrowers" },
  { to: "/due-diligence", label: "Due Diligence", testId: "nav-due-diligence" },
  { to: "/team", label: "Team", testId: "nav-team" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#070B14]/85 border-b border-white/5"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-serif-display text-white text-2xl tracking-[0.32em] uppercase font-light"
        >
          EMIVIA
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-[#D4AF37]" : "text-white/70 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden lg:inline-flex items-center text-[11px] uppercase tracking-[0.28em] border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 hover:bg-[#D4AF37] hover:text-[#070B14] transition-colors duration-300"
        >
          Get in touch
        </Link>

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label="Toggle navigation"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden border-t border-white/5 bg-[#070B14]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`${item.testId}-mobile`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] ${
                    isActive ? "text-[#D4AF37]" : "text-white/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-mobile"
              className="mt-2 inline-flex items-center justify-center text-[11px] uppercase tracking-[0.28em] border border-[#D4AF37] text-[#D4AF37] px-5 py-3"
            >
              Get in touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
